import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, RefreshCw, Eye } from 'lucide-react';

const API_BASE = '/api';

export function Config() {
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftKey, setDraftKey] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchDraftKey();
  }, []);

  const fetchDraftKey = async () => {
    try {
      const response = await fetch(`${API_BASE}/get-draft-key.php`, { credentials: 'include' });
      const result = await response.json();
      if (result.success && result.draft_key) {
        setDraftKey(result.draft_key);
      }
    } catch (e) {
      console.error('Failed to fetch draft key');
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/config.php`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Received data from API:', data);
      console.log('Config object from data:', data.config);
      if (data.success) {
        setConfig(data.config);
        setOriginalConfig(data.config);
      } else {
        toast.error(data.error || 'Nie udało się pobrać konfiguracji');
      }
    } catch (error) {
      toast.error('Nie udało się pobrać konfiguracji');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ config })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Konfiguracja zapisana');
        setOriginalConfig(config);
      } else {
        toast.error(data.error || 'Nie udało się zapisać konfiguracji');
      }
    } catch (error) {
      toast.error('Nie udało się zapisać konfiguracji');
    }
  };

  const handleReset = () => {
    setConfig(originalConfig);
    toast.info('Przywrócono oryginalne ustawienia.');
  }

  const handleArrayChange = (key, index, value) => {
    const newArray = [...(config[key] || [])];
    newArray[index] = value;
    setConfig({ ...config, [key]: newArray });
  };

  const addArrayItem = (key) => {
    const newArray = [...(config[key] || []), ''];
    setConfig({ ...config, [key]: newArray });
  };

  const removeArrayItem = (key, index) => {
    const newArray = [...(config[key] || [])];
    newArray.splice(index, 1);
    setConfig({ ...config, [key]: newArray });
  };

  const handleManualTrigger = async (action, customPayload = {}) => {
    try {
      toast.info(`Wysłano żądanie: ${action}...`);
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, payload: customPayload })
      });
      
      const text = await response.text();
      try {
        const data = JSON.parse(text);
        if (response.ok) {
           toast.success(`Akcja ${action} zakończona sukcesem!`);
           
           // If generating LinkedIn/Reels manually, arm the polling mechanism
           if (action === 'generate-linkedin' || action === 'generate-reels') {
             const today = new Date().toISOString().split('T')[0];
             localStorage.setItem('waiting_for_reels', JSON.stringify({
               date: today,
               started_at: new Date().toISOString()
             }));
             toast.info('Rozpoczęto nasłuchiwanie na wyniki...');
           }
        } else {
           toast.error(`Błąd: ${data.error || data.message || 'Nieznany błąd'}`);
        }
      } catch (e) {
        // If response is not JSON (e.g. n8n returns simple "Workflow started")
        if (response.ok) {
            toast.success(`Akcja ${action} wysłana! Odpowiedź: ${text.substring(0, 50)}...`);
        } else {
            toast.error(`Błąd serwera: ${text.substring(0, 50)}...`);
        }
      }
    } catch (error) {
      toast.error('Błąd połączenia: ' + error.message);
    }
  };

  if (loading) return <div className="p-6">Ładowanie konfiguracji...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Konfiguracja Projektu</h1>

      {/* Developer Tools Section - Always visible */}
      <section className="mb-6 p-4 border rounded-lg bg-slate-50 border-slate-200">
        <h2 className="text-xl font-semibold mb-3 text-slate-800">Narzędzia Deweloperskie (Webhooks)</h2>
        <p className="text-sm text-slate-600 mb-4">
          Ręczne wywoływanie procesów w n8n. Dostępne niezależnie od konfiguracji S3.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleManualTrigger('generate-linkedin')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Wymuś generowanie LinkedIn
          </button>
          
          <button
            onClick={() => handleManualTrigger('generate-reels')}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Wymuś generowanie Reels
          </button>
          
          <button
            onClick={() => handleManualTrigger('generate-social')}
            className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Wymuś generowanie Social
          </button>

          <button
            onClick={() => {
              if (!draftKey) {
                toast.error('Nie znaleziono klucza draftu na dzisiaj. Upewnij się, że draft został utworzony.');
                return;
              }
              handleManualTrigger('generate-intro', { key: draftKey });
            }}
            className={`px-4 py-2 text-white rounded transition-colors flex items-center gap-2 ${draftKey ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'}`}
          >
            <RefreshCw className="w-4 h-4" />
            Wymuś generowanie Intro (Draft)
          </button>
        </div>
      </section>

      {!config ? (
        <div className="p-6 border rounded-lg bg-red-50 text-red-600 border-red-200">
          Nie udało się załadować pliku konfiguracji dla bieżącego projektu.
          <br/>Możesz używać narzędzi deweloperskich powyżej.
        </div>
      ) : (
        <>
      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">RSS Sources</h2>
        {config?.rss_sources?.map((source, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input 
              type="text" 
              value={source}
              onChange={(e) => handleArrayChange('rss_sources', index, e.target.value)}
              className="w-full p-2 border rounded-md"
            />
            <button onClick={() => removeArrayItem('rss_sources', index)} className="text-red-500">X</button>
          </div>
        ))}
        <button onClick={() => addArrayItem('rss_sources')} className="mt-2 px-4 py-2 bg-gray-200 rounded-md">+ Dodaj źródło</button>
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Target Audience Profile</h2>
        <textarea
          className="w-full p-3 border rounded-lg"
          rows={5}
          value={config.target_audience_profile || ''}
          onChange={(e) => setConfig({ ...config, target_audience_profile: e.target.value })}
        />
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Reddit Sources</h2>
        <div className="space-y-2">
          {config?.reddit_sources?.map((source, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                className="flex-1 p-2 border rounded"
                value={source}
                onChange={(e) => {
                  const newSources = [...config.reddit_sources];
                  newSources[index] = e.target.value;
                  setConfig({ ...config, reddit_sources: newSources });
                }}
                placeholder="subreddit name (np. technology)"
              />
              <button
                onClick={() => {
                  const newSources = config.reddit_sources.filter((_, i) => i !== index);
                  setConfig({ ...config, reddit_sources: newSources });
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Usuń
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const newSources = [...(config?.reddit_sources || []), ''];
              setConfig({ ...config, reddit_sources: newSources });
            }}
            className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            + Dodaj Subreddit
          </button>
        </div>
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Keywords Filter</h2>
        <div className="space-y-2">
          {config?.keywords_filter?.map((keyword, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                className="flex-1 p-2 border rounded"
                value={keyword}
                onChange={(e) => {
                  const newKeywords = [...config.keywords_filter];
                  newKeywords[index] = e.target.value;
                  setConfig({ ...config, keywords_filter: newKeywords });
                }}
                placeholder="np. AI, GPT, ChatGPT"
              />
              <button
                onClick={() => {
                  const newKeywords = config.keywords_filter.filter((_, i) => i !== index);
                  setConfig({ ...config, keywords_filter: newKeywords });
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Usuń
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              const newKeywords = [...(config?.keywords_filter || []), ''];
              setConfig({ ...config, keywords_filter: newKeywords });
            }}
            className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            + Dodaj Keyword
          </button>
        </div>
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Newsletter Topic</h2>
        <textarea
          className="w-full p-3 border rounded-lg font-mono text-sm"
          rows={6}
          value={config?.newsletter_topic || ''}
          onChange={(e) => setConfig({
            ...config,
            newsletter_topic: e.target.value
          })}
          placeholder="Opisz temat newslettera, jego cel i wartość dla czytelników..."
        />
        <p className="text-sm text-gray-500 mt-2">
          Opis tematu newslettera używany przez AI do scoringu artykułów
        </p>
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Summary Prompt</h2>
        <textarea
          className="w-full p-3 border rounded-lg font-mono text-sm"
          rows={4}
          value={config?.summary_prompt || ''}
          onChange={(e) => setConfig({
            ...config,
            summary_prompt: e.target.value
          })}
          placeholder="Prompt dla modelu AI do generowania podsumowań artykułów..."
        />
        <p className="text-sm text-gray-500 mt-2">
          Instrukcja dla AI jak streszczać artykuły w newsletterze
        </p>
      </section>

      <section className="mb-6 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">Scoring Criteria</h2>
        <p className="text-sm text-gray-600 mb-3">
          Kryteria scoringu artykułów przez AI. Format: array of objects z id, weight, prompt.
        </p>

        {config?.scoring_criteria?.map((criterion, index) => (
          <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold">Kryterium #{index + 1}</h3>
              <button
                onClick={() => {
                  const newCriteria = config.scoring_criteria.filter((_, i) => i !== index);
                  setConfig({ ...config, scoring_criteria: newCriteria });
                }}
                className="text-red-600 hover:text-red-800"
              >
                Usuń
              </button>
            </div>

            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium mb-1">ID:</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={criterion.id || ''}
                  onChange={(e) => {
                    const newCriteria = [...config.scoring_criteria];
                    newCriteria[index] = { ...criterion, id: e.target.value };
                    setConfig({ ...config, scoring_criteria: newCriteria });
                  }}
                  placeholder="np. business_utility"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Waga (0-1):</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  className="w-full p-2 border rounded"
                  value={criterion.weight || 0}
                  onChange={(e) => {
                    const newCriteria = [...config.scoring_criteria];
                    newCriteria[index] = { ...criterion, weight: parseFloat(e.target.value) };
                    setConfig({ ...config, scoring_criteria: newCriteria });
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prompt:</label>
                <textarea
                  className="w-full p-2 border rounded"
                  rows={3}
                  value={criterion.prompt || ''}
                  onChange={(e) => {
                    const newCriteria = [...config.scoring_criteria];
                    newCriteria[index] = { ...criterion, prompt: e.target.value };
                    setConfig({ ...config, scoring_criteria: newCriteria });
                  }}
                  placeholder="Instrukcja dla AI jak oceniać artykuły według tego kryterium..."
                />
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={() => {
            const newCriterion = {
              id: '',
              weight: 0,
              prompt: ''
            };
            const newCriteria = [...(config?.scoring_criteria || []), newCriterion];
            setConfig({ ...config, scoring_criteria: newCriteria });
          }}
          className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          + Dodaj Kryterium
        </button>
      </section>
      </>
      )}

      <div className="flex gap-4 mt-8">
        <button
          onClick={saveConfig}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
        >
          <Save size={20} />
          Zapisz
        </button>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300"
        >
          <RefreshCw size={20} />
          Reset
        </button>
        <button
          onClick={() => alert(JSON.stringify(config, null, 2))}
          className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
        >
          <Eye size={20} />
          Podgląd JSON
        </button>
      </div>
    </div>
  );
}